import React from 'react';
import type { Case } from '@/lib/supabaseData';
import { formatCurrency, formatDate } from '@/lib/supabaseData';
import StatusBadge from '@/components/ui/StatusBadge';
import CaseDocumentsPublic from './CaseDocumentsPublic';
import CasePdfReport from '@/components/CasePdfReport';
import { User, Phone, Mail, DollarSign, Clock, CheckCircle2, Circle, Calendar, Scale, Building2, Hash } from 'lucide-react';

interface CaseResultCardProps {
  caseData: Case;
}

export default function CaseResultCard({ caseData }: CaseResultCardProps) {
  const statusSteps = [
  'Recibido',
  'En Revisión',
  'En Litigio',
  'Resolución Pendiente',
  'Aprobado',
  'Recuperación en Proceso',
  'Cerrado'] as
  const;

  const currentStepIndex = statusSteps.indexOf(
    caseData.status as (typeof statusSteps)[number]
  );

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Header Card */}
      <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Hash size={14} className="text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase font-tabular">
                {caseData.caseNumber}
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">
              {caseData.claimantName}
            </h2>
            <p className="text-sm text-muted-foreground">
              {caseData.documentType}:{' '}
              <span className="font-semibold text-foreground font-tabular">
                {caseData.documentNumber}
              </span>
            </p>
          </div>
          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2">
            <StatusBadge status={caseData.status} size="lg" />
            <p className="text-xs text-muted-foreground">
              Última actualización: {formatDate(caseData.updatedAt)}
            </p>
          </div>
        </div>

        {/* Progress Steps — horizontal scroll on mobile */}
        <div className="mt-6 pt-5 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            Progreso del proceso
          </p>
          <div className="flex items-center gap-0 overflow-x-auto pb-2 -mx-1 px-1">
            {statusSteps.map((step, idx) => {
              const isDone = idx < currentStepIndex;
              const isCurrent = idx === currentStepIndex;
              const isLast = idx === statusSteps.length - 1;
              return (
                <React.Fragment key={`step-${step.replace(/\s/g, '-')}`}>
                  <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors ${
                        isDone
                          ? 'bg-accent border-accent'
                          : isCurrent
                          ? 'bg-white border-accent' :'bg-white border-border'
                      }`}
                    >
                      {isDone ? (
                        <CheckCircle2 size={14} className="text-white" />
                      ) : isCurrent ? (
                        <Circle size={10} className="text-accent fill-accent" />
                      ) : (
                        <Circle size={10} className="text-muted-foreground/30" />
                      )}
                    </div>
                    <span
                      className={`text-[9px] sm:text-[10px] text-center leading-tight max-w-[56px] sm:max-w-[64px] ${
                        isCurrent
                          ? 'font-bold text-accent'
                          : isDone
                          ? 'font-medium text-muted-foreground'
                          : 'text-muted-foreground/50'
                      }`}
                    >
                      {step}
                    </span>
                  </div>
                  {!isLast && (
                    <div
                      className={`h-0.5 flex-1 min-w-[12px] sm:min-w-[16px] mx-0.5 sm:mx-1 mb-5 rounded-full ${
                        idx < currentStepIndex ? 'bg-accent' : 'bg-border'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info Grid — single col on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Case Type */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
              <Scale size={16} className="text-muted-foreground" />
            </div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Tipo de caso
            </p>
          </div>
          <p className="text-sm font-bold text-foreground">{caseData.caseType}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {caseData.description}
          </p>
        </div>

        {/* Court */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
              <Building2 size={16} className="text-muted-foreground" />
            </div>
          </div>
          <p className="text-sm font-semibold text-foreground leading-snug">
            {caseData.caseNumber}
          </p>
        </div>

        {/* Recovery Amount */}
        <div className="bg-accent/5 border border-accent/20 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <DollarSign size={16} className="text-accent" />
            </div>
            <p className="text-xs font-semibold text-accent uppercase tracking-wider">
              Monto estimado a recuperar
            </p>
          </div>
          <p className="text-2xl font-bold text-foreground font-tabular">
            {formatCurrency(caseData.estimatedRecovery, caseData.currency)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Estimado sujeto a resolución judicial
          </p>
        </div>
      </div>

      {/* Missing Documents Fee */}
      {caseData.missingDocumentsFee > 0 &&
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 shadow-sm flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <DollarSign size={20} className="text-red-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">
              Monto a pagar por documentos faltantes
            </p>
            <p className="text-2xl font-bold text-red-700 font-tabular">
              {formatCurrency(caseData.missingDocumentsFee, caseData.currency)}
            </p>
            <p className="text-xs text-red-600/80 mt-1">
              Debe realizar este pago para continuar con el proceso. Comuníquese con su abogado asignado.
            </p>
          </div>
        </div>
      }

      {/* Attorney Card */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Abogado asignado a su caso
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-foreground">
              {caseData.assignedAttorney.name}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              {caseData.assignedAttorney.specialization}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={`mailto:${caseData.assignedAttorney.email}`}
                className="flex items-center gap-1.5 text-sm text-accent hover:underline">

                <Mail size={14} />
                {caseData.assignedAttorney.email}
              </a>
              <a
                href={`tel:${caseData.assignedAttorney.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-1.5 text-sm text-accent hover:underline">

                <Phone size={14} />
                {caseData.assignedAttorney.phone}
              </a>
            </div>
          </div>
          <div className="text-xs text-muted-foreground text-right flex-shrink-0">
            <p className="font-medium">Ingreso del caso</p>
            <p className="font-tabular">{formatDate(caseData.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <Clock size={16} className="text-muted-foreground" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Historial de actualizaciones del expediente
          </p>
          <span className="ml-auto bg-muted text-muted-foreground text-xs font-bold px-2 py-0.5 rounded-full">
            {caseData.updates.length} entradas
          </span>
        </div>

        <div className="relative">
          <div className="absolute left-3.5 top-0 bottom-0 w-px bg-border" />
          <div className="space-y-6">
            {caseData.updates.map((update, idx) =>
            <div
              key={update.id}
              className="relative pl-10 slide-up"
              style={{ animationDelay: `${idx * 60}ms` }}>

                <div
                className={`absolute left-0 top-1 w-7 h-7 rounded-full border-2 flex items-center justify-center ${
                idx === 0 ?
                'bg-accent border-accent' : 'bg-white border-border'}`
                }>

                  {idx === 0 ?
                <CheckCircle2 size={13} className="text-white" /> :

                <Circle size={8} className="text-muted-foreground/40" />
                }
                </div>
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                  <div className="flex-1">
                    <h4
                    className={`text-sm font-bold mb-1 ${
                    idx === 0 ? 'text-accent' : 'text-foreground'}`
                    }>

                      {update.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {update.description}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-2">
                      Por: {update.author}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-tabular flex-shrink-0">
                    <Calendar size={12} />
                    {formatDate(update.date)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-amber-600 text-xs font-bold">!</span>
        </div>
        <p className="text-xs text-amber-800 leading-relaxed">
          La información aquí presentada es de carácter informativo. Los montos estimados
          están sujetos a la resolución judicial definitiva. Para información oficial
          sobre su caso, comuníquese directamente con su abogado asignado.
        </p>
      </div>

      {/* Documents */}
      <CaseDocumentsPublic caseId={caseData.id} />

      {/* Export PDF */}
      <div className="flex justify-end">
        <CasePdfReport caseData={caseData} />
      </div>
    </div>);

}